// src/components/Layout.jsx
// UPDATED: Uses actual SuddyFlow logo instead of generated icon

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Users, Briefcase, FileText, DollarSign, 
  Settings, Menu, X, LogOut, Mail, UserPlus,
  PieChart, FileSignature, HelpCircle
} from 'lucide-react';
import { useBusinessContext } from '../contexts/BusinessContext';
import api from '../services/api';
import useTheme from '../hooks/useTheme';
import TierBadge from './TierBadge';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingPaymentCount, setPendingPaymentCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    businessName, 
    user, 
    logout, 
    userRole,
    subscriptionPlan,
    isTrialActive,
    trialDaysRemaining,
    shouldShowTierBadges,
     canAccessFeature,
    isSimpleMode,
  } = useBusinessContext();
  const { theme } = useTheme();

  const allNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, roles: ['Owner', 'Employee'], feature: null },
    { name: 'Jobs', href: '/jobs', icon: Briefcase, roles: ['Owner', 'Employee'], feature: null },
    { name: 'Customers', href: '/customers', icon: Users, roles: ['Owner', 'Employee'], feature: null },
    { name: 'Leads', href: '/leads', icon: Mail, roles: ['Owner', 'Employee'], feature: null },
    { name: 'Invoices', href: '/invoices', icon: FileText, roles: ['Owner'], feature: null },
    { name: 'Expenses', href: '/expenses', icon: DollarSign, roles: ['Owner'], feature: null },
    { name: 'Reports', href: '/reports', icon: PieChart, roles: ['Owner'], feature: null },
    { name: 'Agreements', href: '/agreements', icon: FileSignature, roles: ['Owner'], feature: null },
    { name: 'Team', href: '/team', icon: UserPlus, roles: ['Owner'], feature: 'team-members' },
    { name: 'Email Templates', href: '/email-templates', icon: Mail, roles: ['Owner'], feature: 'custom-email-templates' },
    { name: 'Settings', href: '/settings', icon: Settings, roles: ['Owner'], feature: null },
    { name: 'Help', href: '/help', icon: HelpCircle, roles: ['Owner'], feature: null },
  ];

  // Simple mode hides these from the nav (view preference only)
  const SIMPLE_MODE_HIDDEN = ['Leads', 'Agreements', 'Team', 'Email Templates'];

  const navigation = allNavigation.filter(item => {
    if (!item.roles.includes(userRole)) return false;
    if (isSimpleMode && SIMPLE_MODE_HIDDEN.includes(item.name)) return false;
    if (!item.feature) return true;
    if (isTrialActive) return true;
    return canAccessFeature(item.feature);
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const getSubscriptionBadge = () => {
    if (isTrialActive) {
      const urgency = trialDaysRemaining <= 3 ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
      return {
        label: `Trial: ${trialDaysRemaining}d left`,
        className: urgency,
      };
    }
    
    if (subscriptionPlan === 'Pro') {
      return { label: 'PRO', className: 'bg-blue-100 text-blue-700' };
    }
    
    if (subscriptionPlan === 'Enterprise') {
      return { label: 'ENTERPRISE', className: 'bg-purple-100 text-purple-700' };
    }
    
    return { label: 'SOLO', className: 'bg-gray-100 text-gray-700' };
  };

  const subBadge = getSubscriptionBadge();

  // Fetch pending payment count for badge
  React.useEffect(() => {
    const fetchPendingCount = async () => {
      if (userRole !== 'Owner') return;
      
      // Make sure we have a token before calling API
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const response = await api.get('/PendingPayments/count');
        setPendingPaymentCount(response.data.count || 0);
      } catch (error) {
        // Silently fail - badge just won't show
        console.log('Could not fetch pending payment count');
      }
    };
    fetchPendingCount();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchPendingCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userRole, location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-30">
        <div className="flex items-center justify-between px-4 h-16">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <div className="flex items-center gap-2">
            <img 
              src="/images/Icon-PNG.png" 
              alt="SuddyFlow" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-lg">SuddyFlow</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${subBadge.className}`}>
              {subBadge.label}
            </span>
          </div>
          
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-primary-700 opacity-5"></div>
            
            <div className="relative flex items-center gap-3 px-6 h-16 border-b border-gray-200">
              <img 
                src="/images/Icon-PNG.png" 
                alt="SuddyFlow" 
                className="w-10 h-10 object-contain"
              />
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-lg truncate">SuddyFlow</h1>
                <p className="text-xs text-gray-500 truncate">{businessName}</p>
              </div>
            </div>
            
            {/* Subscription & Role badges */}
            <div className="px-6 py-3 bg-gradient-to-r from-primary-50 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                  <span className="font-medium text-primary-700">
                    {theme?.displayName || 'Professional'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${subBadge.className}`}>
                    {subBadge.label}
                  </span>
                  {userRole && (
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                      userRole === 'Owner' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {userRole}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    active
                      ? 'bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 shadow-sm'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon 
                    size={20} 
                    className={active ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-700'}
                  />
                  <span className="flex-1">{item.name}</span>
                  
                  {/* Pending payment badge for Invoices */}
                  {item.name === 'Invoices' && pendingPaymentCount > 0 && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                      {pendingPaymentCount}
                    </span>
                  )}
                  
                  {shouldShowTierBadges && item.feature && (
                    <TierBadge feature={item.feature} size="xs" />
                  )}
                  
                  {active && (
                    <div className="w-1 h-6 bg-primary-600 rounded-full"></div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Trial CTA */}
          {isTrialActive && userRole === 'Owner' && (
            <div className="px-3 py-3">
              <Link
                to="/subscribe"
                className="block w-full text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 px-4 rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm"
              >
                Upgrade Now
              </Link>
            </div>
          )}

          {/* User section */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="mb-3 px-3 py-2 bg-white rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-white hover:shadow-sm transition-all"
            >
              <LogOut size={20} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="pt-16 pb-20 lg:pt-0 lg:pb-0">
          {children}
        </div>
      </main>

      {/* Mobile bottom navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-20 shadow-lg">
        <div className={`grid h-16 ${navigation.length >= 5 ? 'grid-cols-5' : navigation.length === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {navigation.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center py-2 text-gray-600 hover:text-primary-600 transition-colors relative ${
                  active ? 'text-primary-600' : ''
                }`}
              >
                <div className="relative">
                  <Icon size={22} strokeWidth={active ? 2.5 : 2} />
                  {shouldShowTierBadges && item.feature && (
                    <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                      item.feature && ['api-access', 'advanced-analytics', 'multi-location', 'custom-branding', 'custom-email-templates', 're-engagement', 'unlimited-team'].includes(item.feature)
                        ? 'bg-purple-500'
                        : 'bg-blue-500'
                    }`} />
                  )}
                </div>
                <span className="text-xs mt-1 font-medium">{item.name}</span>
                {active && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-primary-600 rounded-t-full"></div>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;